import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../../api/client'
import {
  chooseTicketTypePriority,
  setTicketTypeDefaultPriority,
  ticketTypePrioritiesQuery,
} from '../../api/settingsEaseedeskTicketTypes'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket priorities by type.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_types/priorities/index.php`.
 *
 * ---------------------------------------------------------------------------
 * CE INSEAMNA CELE DOUA COLOANE
 * ---------------------------------------------------------------------------
 *
 * "Priority on new tickets" e prioritatea cu care PORNESTE un tichet al
 * tipului: la formularul de tichet nou vine deja aleasa si ramane schimbabila
 * de mana, iar la tichetele care nu trec prin formular (email, chat, alarma) se
 * pune ea, fiindca altfel n-ar avea de unde s-o ia. Pe un tichet DESCHIS nu se
 * atinge nimic.
 *
 * "Priorities the agent can pick" filtreaza dropdownul de pe tichet. NICIO BIFA
 * INSEAMNA TOATE: filtrarea porneste tip cu tip, cand cineva chiar are ceva de
 * spus - un Task n-are ce cauta cu "Critical" - si nu goleste dropdownul
 * nimanui in ziua livrarii.
 *
 * ---------------------------------------------------------------------------
 * DOUA MIGRARI CARE POT LIPSI
 * ---------------------------------------------------------------------------
 *
 * `migrated` si `chooseAvailable`. Cat timp coloana sau tabela lipsesc, ecranul
 * o spune si nu scrie nimic - portalul si migrarile se urca separat.
 */
export function TicketTypePrioritiesPage() {
  const queryClient = useQueryClient()
  const page = useQuery(ticketTypePrioritiesQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_type_priorities'] })
  }

  const setDefault = useMutation({
    mutationFn: (choice: { type: number; priority: number }) =>
      setTicketTypeDefaultPriority(choice.type, choice.priority),
    onSuccess: () => {
      toast('Starting priority saved.', 'Success')
      reload()
    },
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      reload()
    },
  })

  const choose = useMutation({
    mutationFn: (choice: { type: number; priority: number; chosen: boolean }) =>
      chooseTicketTypePriority(choice.type, choice.priority, choice.chosen),
    onSuccess: () => reload(),
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      reload()
    },
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="The ticket priorities" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  const inactive = new Map(page.data.inactive.map((row) => [row.ID, row.priority_name]))

  return (
    <div>
      <SettingsHead
        strong="Ticket"
        rest="priorities by type"
        subtitle={
          <>
            With what <strong>priority</strong> a ticket of each type starts
          </>
        }
      />

      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> A new ticket of the type starts with the priority
        chosen here. It can still be changed by hand before the ticket is created, and tickets that are already open are
        not touched.
      </div>

      {!page.data.migrated ? (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> This setting needs a database update that
          has not been applied yet. Until then nothing is saved here.
        </div>
      ) : null}

      <table className="table table-hover dataTable">
        <thead>
          <tr>
            <th width="100px"></th>
            <th className="align-left">Ticket type</th>
            <th className="align-left" style={{ width: 280 }}>
              Priority on new tickets
            </th>
            <th className="align-left">Priorities the agent can pick</th>
          </tr>
        </thead>
        <tbody>
          {page.data.types.map((type) => {
            const selected = type.default_priority
            const gone = selected > 0 && inactive.has(selected)

            return (
              <tr key={type.ID}>
                <td className="align-center">
                  {/* `od_console.css` da fiecarui `.badge` cerneala inchisa
                      (`--od-ink-soft`) si macheta scrie doar fundalul:
                      prescurtarea se citea inchis pe cercul colorat. */}
                  <span
                    className="badge"
                    style={{
                      minWidth: 23,
                      backgroundColor: type.color_identifier,
                      color: '#fff',
                      textAlign: 'center',
                    }}
                  >
                    {type.short_name}
                  </span>
                </td>
                <td className="align-left">{type.type_name}</td>
                <td className="align-left">
                  <select
                    name="priority"
                    className="form-control"
                    aria-label={type.type_name + ' - priority on new tickets'}
                    disabled={!page.data.migrated}
                    value={selected}
                    onChange={(e) => setDefault.mutate({ type: type.ID, priority: Number(e.target.value) })}
                  >
                    <option value={0}>No automatic priority</option>
                    {page.data.priorities.map((priority) => (
                      <option key={priority.ID} value={priority.ID}>
                        {priority.priority_name}
                      </option>
                    ))}
                    {/* Prioritatea setata candva si scoasa intre timp din uz: se
                        arata, ca sa se vada ce scrie in setare, dar se spune
                        limpede ca nu se mai aplica. */}
                    {gone ? (
                      <option value={selected}>{inactive.get(selected)} (inactive - not applied)</option>
                    ) : null}
                  </select>
                </td>
                <td className="align-left">
                  <div className="type-priority-choice" data-type={type.ID}>
                    {page.data.priorities.map((priority) => (
                      <label
                        key={priority.ID}
                        style={{ fontWeight: 'normal', margin: '0 12px 4px 0', display: 'inline-block' }}
                      >
                        <input
                          type="checkbox"
                          className="choose-priority"
                          value={priority.ID}
                          disabled={!page.data.chooseAvailable}
                          checked={type.chosen.includes(priority.ID)}
                          onChange={(e) =>
                            choose.mutate({ type: type.ID, priority: priority.ID, chosen: e.target.checked })
                          }
                        />{' '}
                        {/* Aceeasi poveste ca la insigna: pastila zonei are
                            cerneala inchisa, iar macheta scrie doar culoarea
                            prioritatii. In portal scrisul era alb. */}
                        <span
                          className="label"
                          style={{ backgroundColor: priority.priority_color, color: '#fff' }}
                        >
                          {priority.priority_name}
                        </span>
                      </label>
                    ))}
                    <div className="color-light-grey type-priority-hint" style={{ fontSize: 11, marginTop: 2 }}>
                      {type.chosen.length > 0 ? 'only these' : 'nothing ticked — the agent sees all of them'}
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
          {page.data.types.length === 0 ? (
            <tr>
              <td colSpan={4} className="color-light-grey">
                There is no ticket type on this instance.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}
